import React, { useState, useEffect } from 'react';
import './OrderModals.css';
import Modal from '../Modal';
import ShareButton from '../ShareButton';
import { openFileWithNativeApp } from '../../utils/fileViewer';
import { isImageFile, isPdfFile } from '../../utils/fileUtils';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from '../../hooks/useTranslation';
import styles from '../modals/ModalForm.module.css';
import { useError } from '../../contexts/ErrorContext';
import PlatformText from '../PlatformText';
import { isAndroid, isIOS } from '../../utils/platformUtils';

// Platform detection
const isNative = Capacitor.isNativePlatform();
const isWeb = !isNative;
const isAndroidNative = isNative && isAndroid();
const isIOSNative = isNative && isIOS();

// Enhanced DocumentViewer component with native support
const DocumentViewer = ({ url, type }) => {
  const { t } = useTranslation('orders');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfLoadError, setPdfLoadError] = useState(false);
  
  const isImage = isImageFile(url);
  const isPDF = isPdfFile(url);

  const handleNativeOpen = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await openFileWithNativeApp(url, `${type.toLowerCase()}`);
      
      if (!result.success) {
        setError(t('errors.openFileFailed'));
      }
    } catch (error) {
      // Error opening file - handled silently
      setError(t('errors.openFileError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle PDF iframe load events
  const handlePdfLoad = () => {
    // Reset error state on successful load
    setPdfLoadError(false);
  };

  const handlePdfError = () => {
    // Set error state if iframe fails to load
    setPdfLoadError(true);
  };

  // Reset PDF error state when URL changes (only for iOS native)
  useEffect(() => {
    if (isPDF && isIOSNative) {
      setPdfLoadError(false);
    }
  }, [url, isPDF, isIOSNative]);

  return (
    <div className="document-viewer">
      {isImage ? (
        // Image Viewer - works on all platforms
        <img
          src={url}
          alt={type}
          className="document-viewer-image"
        />
      ) : isPDF && !isNative ? (
        // PDF Viewer - only on web platforms (PWA/Web)
        <object data={url} type="application/pdf" width="100%" height="500px">
          <p>Your browser doesn't support PDF viewing. 
            <a href={url} target="_blank" rel="noopener noreferrer">Click here to view/download the {type}</a>
          </p>
        </object>
      ) : isPDF && isAndroidNative ? (
        // PDF Viewer for native Android - Android WebView doesn't support PDF rendering in iframe
        // Use native file opener directly
        <div className="native-file-viewer">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="file-preview-placeholder">
              <div className="file-icon">📄</div>
              <h4>{type}</h4>
              <p>
                <PlatformText translationKey="modals.fileOpenInDefaultApp" namespace="orders" />
              </p>
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
              <button 
                className={`native-open-button ${isLoading ? 'loading' : ''}`} 
                onClick={handleNativeOpen}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="loading-spinner"></span>
                    <PlatformText translationKey="opening" namespace="common" />
                  </>
                ) : (
                  <PlatformText translationKey="open" namespace="common" />
                )}
              </button>
            </div>
          </div>
        </div>
      ) : isPDF && isIOSNative ? (
        // PDF Viewer for native iOS - iOS WKWebView supports iframe PDF rendering
        <div className="pdf-viewer-container">
          {pdfLoadError ? (
            // Fallback to "Open" button if iframe fails to load
            <div className="native-file-viewer">
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div className="file-preview-placeholder">
                  <div className="file-icon">📄</div>
                  <h4>{type}</h4>
                  <p>
                    <PlatformText translationKey="modals.fileOpenInDefaultApp" namespace="orders" />
                  </p>
                  {error && (
                    <div className="error-message">
                      {error}
                    </div>
                  )}
                  <button 
                    className={`native-open-button ${isLoading ? 'loading' : ''}`} 
                    onClick={handleNativeOpen}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="loading-spinner"></span>
                        <PlatformText translationKey="opening" namespace="common" />
                      </>
                    ) : (
                      <PlatformText translationKey="open" namespace="common" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <iframe
              src={url}
              type="application/pdf"
              className="document-viewer-pdf"
              title={type}
              onLoad={handlePdfLoad}
              onError={handlePdfError}
            />
          )}
        </div>
      ) : (
        // Native file opener for other file types, fallback for web
        <div className="native-file-viewer">
          {isNative ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="file-preview-placeholder">
                <div className="file-icon">
                  {isPDF ? '📄' : '📎'}
                </div>
                <h4>{type}</h4>
                <p>
                  <PlatformText translationKey="modals.fileOpenInDefaultApp" namespace="orders" />
                </p>
                {error && (
                  <div className="error-message">
                    {error}
                  </div>
                )}
                <button 
                  className={`native-open-button ${isLoading ? 'loading' : ''}`} 
                  onClick={handleNativeOpen}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="loading-spinner"></span>
                      <PlatformText translationKey="opening" namespace="common" />
                    </>
                  ) : (
                    <PlatformText translationKey="open" namespace="common" />
                  )}
                </button>
              </div>
            </div>
          ) :
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="file-preview-placeholder">
                <div className="file-icon">📎</div>
                <h4>{type}</h4>
                <p>
                  <PlatformText translationKey="modals.fileCannotPreview" namespace="orders" />
                </p>
                <button className="native-open-button" onClick={handleNativeOpen}>
                  <PlatformText translationKey="open" namespace="common" />
                </button>
              </div>
            </div>
          }
        </div>
      )}
    </div>
  );
};

function OrderModals({
  editingOrder,
  setEditingOrder,
  editFormData,
  handleEditSubmit,
  handleEditChange,
  billModalUrl,
  setBillModalUrl,
  biltyModalUrl,
  setBiltyModalUrl,
  challanModalUrl,
  setChallanModalUrl,
  productModalUrl,
  setProductModalUrl,
  orderDetailsModalUrl,
  setOrderDetailsModalUrl,
  currentOrderId,
  setCurrentOrderId,
  currentTab,
  userRole,
  handleReplaceBill,
  handleReplaceBilty,
  handleReplaceChallan,
  handleUploadBill,
  handleUploadChallan,
  handleUploadBilty,
  deleteOrderDetailsImage,
  replaceOrderDetailsImage,
  handleReplaceProduct,
  handleUploadProduct,
  organizationData
}) {
  const { t } = useTranslation('orders');
  const { showErrorToast } = useError();
  const canViewBill = () => {
    if (userRole === 'InventoryManager') {
      return organizationData?.allowInventoryManagerBillAccess ?? true;
    }
    return true;
  };

  const handleDeleteOrderDetailsImage = async (orderId, imageUrl) => {
    if (window.confirm(t('errors.confirmDeleteImage'))) {
      if (typeof deleteOrderDetailsImage === 'function') {
        await deleteOrderDetailsImage(orderId, imageUrl);
        setBillModalUrl(null);
        setCurrentOrderId(null);
      } else {
        showErrorToast(t('errors.deleteHandlerNotImplemented'), {
          variant: 'warning',
          autoHide: true,
          duration: 4000
        });
      }
    }
  };
  const handleReplaceOrderDetailsImage = async (orderId, imageUrl) => {
    if (typeof replaceOrderDetailsImage === 'function') {
      await replaceOrderDetailsImage(orderId, imageUrl);
      setBillModalUrl(null);
      setCurrentOrderId(null);
    } else {
      showErrorToast(t('errors.replaceHandlerNotImplemented'), {
        variant: 'warning',
        autoHide: true,
        duration: 4000
      });
    }
  };
  
  const closeBillModal = () => {
    setBillModalUrl(null);
    setCurrentOrderId(null);
  };

  return (
    <>
      {/* Edit Modal */}
      <Modal isOpen={!!editingOrder} onClose={() => setEditingOrder(null)} title={t('modals.editOrder')}>
        <form onSubmit={handleEditSubmit} className={`${styles.modalForm} form-modal`}>
          <div className={styles.field}>
            <label>
              <PlatformText translationKey="form.customerName" namespace="orders" />
            </label>
            <input type="text" name="partyName" value={editFormData.partyName || ''} onChange={handleEditChange} required className={styles.input} />
          </div>
          <div className={styles.field}>
            <label>
              <PlatformText translationKey="orderDetails" namespace="orders" />
            </label>
            <textarea name="orderDetails" value={editFormData.orderDetails || ''} onChange={handleEditChange} required className={styles.input} />
          </div>
          <div className={styles.field}>
            <label>
              <PlatformText translationKey="form.additionalNotes" namespace="orders" />
            </label>
            <textarea name="additionalNotes" value={editFormData.additionalNotes || ''} onChange={handleEditChange} className={styles.input} />
          </div>
          <div className="modal-actions">
            <button type="submit" className="save">
              <PlatformText translationKey="saveChanges" namespace="common" />
            </button>
            <button type="button" onClick={() => setEditingOrder(null)} className="cancel">
              <PlatformText translationKey="cancel" namespace="common" />
            </button>
          </div>
        </form>
      </Modal>

      {/* Bill View Modal */}
      <Modal isOpen={!!billModalUrl && canViewBill()} onClose={closeBillModal} title="Bill View">
        {Array.isArray(billModalUrl) ? (
          billModalUrl.map((url, index) => (
            <div key={index} className="challan-container">
              <div className="document-header">
                <h3 className="document-title">Bill Document</h3>
                <span className="document-index">{index + 1}</span>
              </div>
              <DocumentViewer url={url} type={`Bill ${index + 1}`} />
              <div className={`challan-actions${isNative ? ' native-row-actions' : ''}`}>
                {isWeb && (
                <a href={url} download target="_blank" rel="noopener noreferrer" className="download-button">Download</a>
                )}
                {isNative && (
                  <ShareButton 
                    url={url} 
                    fileName={`bill-${index + 1}`} 
                    title="Share Bill"
                    variant="primary"
                    className="file-action-button"
                  >
                    Share
                  </ShareButton>
                )}
                {(userRole === 'Accountant' || userRole === 'BusinessOwner' || userRole === 'Salesperson') && (
                  <button onClick={() => handleReplaceBill(currentOrderId, index)} className="replace">
                    Replace
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="challan-container">
            <div className="document-header">
              <h3 className="document-title">
                {typeof billModalUrl === 'string' && billModalUrl.includes('orderDetails') ? 'Order Details Image' : 'Bill Document'}
              </h3>
            </div>
            <DocumentViewer url={billModalUrl} type={typeof billModalUrl === 'string' && billModalUrl.includes('orderDetails') ? 'Image' : 'Bill'} />
            {typeof billModalUrl === 'string' && billModalUrl.includes('orderDetails') ? (
              <div className={`challan-actions${isNative ? ' native-row-actions' : ''}`}>
                {isWeb && (
                <a href={billModalUrl} download={'order_details_image'} target="_blank" rel="noopener noreferrer" className="download-button">Download</a>
                )}
                {isNative && (
                  <ShareButton 
                    url={billModalUrl} 
                    fileName="order_details_image" 
                    title={t('modals.shareImage')}
                    variant="primary"
                    className="file-action-button"
                  >
                    Share
                  </ShareButton>
                )}
                <button className="replace" onClick={() => handleReplaceOrderDetailsImage(currentOrderId, billModalUrl)}>
                  Replace
                </button>
                <button className="delete" onClick={() => handleDeleteOrderDetailsImage(currentOrderId, billModalUrl)}>Delete</button>
              </div>
            ) : (
              <div className={`challan-actions${isNative ? ' native-row-actions' : ''}`}>
                {isWeb && (
                <a href={billModalUrl} download={'bill'} target="_blank" rel="noopener noreferrer" className="download-button">Download Bill</a>
                )}
                {isNative && (
                  <ShareButton 
                    url={billModalUrl} 
                    fileName="bill" 
                    title="Share Bill"
                    variant="primary"
                    className="file-action-button"
                  >
                    Share
                  </ShareButton>
                )}
              </div>
            )}
          </div>
        )}
        <div className="modal-actions" style={{ flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            {(userRole === 'Accountant' || userRole === 'BusinessOwner') &&
              !(Array.isArray(billModalUrl) && billModalUrl[0]?.includes('orderDetails')) && (
                <>
                  <button onClick={() => { closeBillModal(); handleReplaceBill(currentOrderId); }} className="replace">Replace all</button>
                  <button onClick={() => { closeBillModal(); handleUploadBill(currentOrderId); }} className="add">Add Bill</button>
                </>
            )}
          </div>
        </div>
      </Modal>

      {/* Challan View Modal */}
      <Modal isOpen={!!challanModalUrl} onClose={() => { setChallanModalUrl(null); setCurrentOrderId(null); }} title="Challan View">
          {Array.isArray(challanModalUrl) ? (
            challanModalUrl.map((url, index) => (
              <div key={index} className="challan-container">
                <div className="document-header">
                  <h3 className="document-title">Challan Document</h3>
                  <span className="document-index">{index + 1}</span>
                </div>
                <DocumentViewer url={url} type={`Challan ${index + 1}`} />
                <div className={`challan-actions${isNative ? ' native-row-actions' : ''}`}>
                  {isWeb && (
                  <a href={url} download target="_blank" rel="noopener noreferrer" className="download-button">Download</a>
                  )}
                  {isNative && (
                    <ShareButton 
                      url={url} 
                      fileName={`challan-${index + 1}`} 
                      title="Share Challan"
                      variant="primary"
                      className="file-action-button"
                    >
                      Share
                    </ShareButton>
                  )}
                  {(userRole === 'InventoryManager' || userRole === 'BusinessOwner' || userRole === 'Accountant') && (
                    <button onClick={() => handleReplaceChallan(currentOrderId, index)} className="replace">
                      Replace
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="challan-container">
              <div className="document-header">
                <h3 className="document-title">Challan Document</h3>
              </div>
              <DocumentViewer url={challanModalUrl} type="Challan" />
              <div className={`challan-actions${isNative ? ' native-row-actions' : ''}`}>
                {isWeb && (
                <a href={challanModalUrl} download="challan" target="_blank" rel="noopener noreferrer" className="download-button">Download</a>
                )}
                {isNative && (
                  <ShareButton 
                    url={challanModalUrl} 
                    fileName="challan" 
                    title="Share Challan"
                    variant="primary"
                    className="file-action-button"
                  >
                    Share
                  </ShareButton>
                )}
              </div>
            </div>
          )}
          <div className="modal-actions" style={{ flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              {(userRole === 'InventoryManager' || userRole === 'BusinessOwner' || userRole === 'Accountant') && (
                <>
                  <button onClick={() => { setChallanModalUrl(null); setCurrentOrderId(null); handleReplaceChallan(currentOrderId); }} className="replace">Replace all</button>
                  <button onClick={() => { setChallanModalUrl(null); setCurrentOrderId(null); handleUploadChallan(currentOrderId); }} className="add">Add Challan</button>
                </>
              )}
            </div>
          </div>
      </Modal>

      {/* Bilty View Modal */}
      <Modal isOpen={!!biltyModalUrl} onClose={() => { setBiltyModalUrl(null); setCurrentOrderId(null); }} title="Bilty View">
        {Array.isArray(biltyModalUrl) ? (
          biltyModalUrl.map((url, index) => (
            <div key={index} className="challan-container">
              <div className="document-header">
                <h3 className="document-title">Bilty Document</h3>
                <span className="document-index">{index + 1}</span>
              </div>
              <DocumentViewer url={url} type={`Bilty ${index + 1}`} />
              <div className={`challan-actions${isNative ? ' native-row-actions' : ''}`}>
                {isWeb && (
                <a href={url} download={`bilty-${index + 1}`} target="_blank" rel="noopener noreferrer" className="download-button">Download Bilty {index + 1}</a>
                )}
                {isNative && (
                  <ShareButton 
                    url={url} 
                    fileName={`bilty-${index + 1}`} 
                    title="Share Bilty"
                    variant="primary"
                    className="file-action-button"
                  >
                    Share
                  </ShareButton>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="challan-container">
            <div className="document-header">
              <h3 className="document-title">Bilty Document</h3>
            </div>
            <DocumentViewer url={biltyModalUrl} type="Bilty" />
            <div className={`challan-actions${isNative ? ' native-row-actions' : ''}`}>
              {isWeb && (
              <a href={biltyModalUrl} download="bilty" target="_blank" rel="noopener noreferrer" className="download-button">Download Bilty</a>
              )}
              {isNative && (
                <ShareButton 
                  url={biltyModalUrl} 
                  fileName="bilty" 
                  title="Share Bilty"
                  variant="primary"
                  className="file-action-button"
                >
                  Share
                </ShareButton>
              )}
            </div>
          </div>
        )}
        <div className="modal-actions" style={{ flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            {(userRole === 'InventoryManager' || userRole === 'BusinessOwner' || userRole === 'Accountant') && (
              <>
                <button onClick={() => { setBiltyModalUrl(null); setCurrentOrderId(null); handleReplaceBilty(currentOrderId); }} className="replace">Replace all</button>
                <button onClick={() => { setBiltyModalUrl(null); setCurrentOrderId(null); handleUploadBilty(currentOrderId); }} className="add">Add Bilty</button>
              </>
            )}
          </div>
        </div>
      </Modal>

      {/* Product Image Modal */}
      <Modal isOpen={!!productModalUrl} onClose={() => { setProductModalUrl(null); setCurrentOrderId(null); }} title="Product Image">
        {Array.isArray(productModalUrl) ? (
          productModalUrl.map((url, index) => (
            <div key={index} className="challan-container">
              <div className="document-header">
                <h3 className="document-title">Product Image</h3>
                <span className="document-index">{index + 1}</span>
              </div>
              <DocumentViewer url={url} type={`Product Image ${index + 1}`} />
              <div className={`challan-actions${isNative ? ' native-row-actions' : ''}`}>
                {isWeb && (
                <a href={url} download={`product-${index + 1}`} target="_blank" rel="noopener noreferrer" className="download-button">Download Image {index + 1}</a>
                )}
                {isNative && (
                  <ShareButton 
                    url={url} 
                    fileName={`product-${index + 1}`} 
                    title={t('modals.shareProductImage')}
                    variant="primary"
                    className="file-action-button"
                  >
                    Share
                  </ShareButton>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="challan-container">
            <div className="document-header">
              <h3 className="document-title">Product Image</h3>
            </div>
            <DocumentViewer url={productModalUrl} type="Product Image" />
            <div className={`challan-actions${isNative ? ' native-row-actions' : ''}`}>
              {isWeb && (
              <a href={productModalUrl} download="product" target="_blank" rel="noopener noreferrer" className="download-button">Download Image</a>
              )}
              {isNative && (
                <ShareButton 
                  url={productModalUrl} 
                  fileName="product" 
                  title="Share Product Image"
                  variant="primary"
                  className="file-action-button"
                >
                  Share
                </ShareButton>
              )}
            </div>
          </div>
        )}
        <div className="modal-actions" style={{ flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            {userRole === 'InventoryManager' && (
              <>
                <button onClick={() => { setProductModalUrl(null); setCurrentOrderId(null); handleReplaceProduct(currentOrderId); }} className="replace">Replace all</button>
                <button onClick={() => { setProductModalUrl(null); setCurrentOrderId(null); handleUploadProduct(currentOrderId); }} className="add">Add Image</button>
              </>
            )}
          </div>
        </div>
      </Modal>

      {/* Order Details Image Modal */}
      <Modal isOpen={!!orderDetailsModalUrl} onClose={() => { setOrderDetailsModalUrl(null); setCurrentOrderId(null); }} title={t('modals.orderDetailsImages')}>
        {Array.isArray(orderDetailsModalUrl) ? (
          orderDetailsModalUrl.map((url, index) => (
            <div key={index} className="challan-container">
              <div className="document-header">
                <h3 className="document-title">{t('modals.orderDetailsImage')}</h3>
                <span className="document-index">{index + 1}</span>
              </div>
              <DocumentViewer url={url} type={`Order Details Image ${index + 1}`} />
              <div className={`challan-actions${isNative ? ' native-row-actions' : ''}`}>
                {isWeb && (
                <a href={url} download={`order-details-${index + 1}`} target="_blank" rel="noopener noreferrer" className="download-button">{t('modals.download')}</a>
                )}
                {isNative && (
                  <ShareButton 
                    url={url} 
                    fileName={`order-details-${index + 1}`} 
                    title={t('modals.shareOrderDetailsImage')}
                    variant="primary"
                    className="file-action-button"
                  >
                    {t('modals.share')}
                  </ShareButton>
                )}
                <button className="replace" onClick={() => handleReplaceOrderDetailsImage(currentOrderId, url)}>
                  {t('modals.replace')}
                </button>
                <button className="delete" onClick={() => handleDeleteOrderDetailsImage(currentOrderId, url)}>{t('modals.delete')}</button>
              </div>
            </div>
          ))
        ) : (
          <div className="challan-container">
            <div className="document-header">
              <h3 className="document-title">{t('modals.orderDetailsImage')}</h3>
            </div>
            <DocumentViewer url={orderDetailsModalUrl} type={t('modals.orderDetailsImage')} />
            <div className={`challan-actions${isNative ? ' native-row-actions' : ''}`}>
              {isWeb && (
              <a href={orderDetailsModalUrl} download="order-details" target="_blank" rel="noopener noreferrer" className="download-button">{t('modals.download')}</a>
              )}
              {isNative && (
                <ShareButton 
                  url={orderDetailsModalUrl} 
                  fileName="order-details" 
                  title={t('modals.shareOrderDetailsImage')}
                  variant="primary"
                  className="file-action-button"
                >
                  {t('modals.shareImage')}
                </ShareButton>
              )}
              <button className="replace" onClick={() => handleReplaceOrderDetailsImage(currentOrderId, orderDetailsModalUrl)}>
                {t('modals.replace')}
              </button>
              <button className="delete" onClick={() => handleDeleteOrderDetailsImage(currentOrderId, orderDetailsModalUrl)}>{t('modals.delete')}</button>
            </div>
          </div>
        )}
        <div className="modal-actions" style={{ flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button onClick={() => { setOrderDetailsModalUrl(null); setCurrentOrderId(null); }} className="replace">
              {t('modals.replaceAll') === 'Replace All' ? 'Replace all' : t('modals.replaceAll')}
            </button>
            <button onClick={() => { setOrderDetailsModalUrl(null); setCurrentOrderId(null); }} className="add">{t('modals.addImage')}</button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default OrderModals; 